import { useState, useEffect } from 'react';
import { commentService } from '../services/commentService';
import { Ticket } from '../types/ticket';

interface CommentCounts {
  [ticketId: number]: number;
}

/**
 * Custom hook to fetch comment counts for tickets
 * @param tickets Array of tickets to fetch comment counts for
 * @returns Object mapping ticket IDs to comment counts
 */
export function useTicketComments(tickets: Ticket[]) {
  const [commentCounts, setCommentCounts] = useState<CommentCounts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if no tickets
    if (!tickets || tickets.length === 0) return;

    const fetchCommentCounts = async () => {
      setLoading(true);
      setError(null);
      
      const counts: CommentCounts = {};
      
      try {
        // Process tickets in batches to avoid too many concurrent requests
        const batchSize = 5;
        for (let i = 0; i < tickets.length; i += batchSize) {
          const batch = tickets.slice(i, i + batchSize);
          
          // Create an array of promises for this batch
          const promises = batch.map(async (ticket) => {
            try {
              const comments = await commentService.getTicketComments(ticket.id);
              counts[ticket.id] = comments.length;
            } catch (err) {
              console.error(`Error fetching comments for ticket ${ticket.id}:`, err);
              counts[ticket.id] = 0; // Default to 0 on error
            }
          });
          
          // Wait for all promises in this batch to resolve
          await Promise.all(promises);
        }
        
        setCommentCounts(counts);
      } catch (err) {
        console.error('Error fetching comment counts:', err);
        setError('Failed to fetch comment counts');
      } finally {
        setLoading(false);
      }
    };

    fetchCommentCounts();
  }, [tickets]);

  return { commentCounts, loading, error };
}
